import { useThemeStore, type ThemeProfile, type ThemeState } from './theme-store'
import { applyMotion, applyNightMode, applySkin, applyThemeVariables } from './apply-theme'
import { waitForSkin } from './skin'
import { isNightActive, nightVariant } from './night'
import { lightVariant } from './light'

type ShownState = Pick<ThemeState, 'lightMode' | 'nightMode' | 'nightSchedule' | 'getActiveProfile'>

/**
 * What is on screen: the active theme, or its light or night version.
 * Forced light wins over night mode, even a scheduled one: it is an explicit,
 * in-the-moment choice (a sunny room at 22:00).
 */
export function shownTheme(state: ShownState, now: Date = new Date()): { profile: ThemeProfile; night: boolean } {
  const profile = state.getActiveProfile()
  if (state.lightMode) return { profile: lightVariant(profile), night: false }
  const night = isNightActive(state.nightMode, state.nightSchedule, now)
  return { profile: night ? nightVariant(profile) : profile, night }
}

/** Apply everything the theme state decides, at time `now`. Shared with ThemeInjector. */
export function applyThemeState(state: ThemeState, now: Date = new Date()) {
  const { profile, night } = shownTheme(state, now)
  applyThemeVariables(profile)
  applyNightMode(night)
  applyMotion(state.motion)
}

/**
 * Applies the persisted theme synchronously, before React mounts. Zustand's
 * `persist` middleware hydrates from localStorage as soon as the store module
 * is evaluated, so `getState()` already reflects the user's last theme — no
 * flash of the default one. The skin's attributes go on too: nothing is on
 * screen yet, and main.tsx waits for the skin's stylesheet before rendering.
 */
export function applyBootstrapTheme() {
  const state = useThemeStore.getState()
  applyThemeState(state)
  applySkin(state.skin, state.thickness)
}

/**
 * Resolves when the saved skin's stylesheet is in (at once for the flat
 * skin), or after `ms` at most: a skin never holds the app back for long.
 * Without the wait, every start with a skin would show a frame of the flat one.
 */
export function bootstrapSkinReady(ms?: number): Promise<void> {
  return waitForSkin(useThemeStore.getState().skin, ms)
}
